import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Flex, InputNumber, Modal, Typography}                from "antd";
import {Form, Input, Alert}           from "antd";
import {useTranslation}               from "react-i18next";
import type {IUser}                         from "@interface/IUser";
import useApi                         from "@hooks/useApi"
import selector         from "@tools/Selectors";
import {valueExtractor} from "@components/form/formUtils";
import passwordValidator from "@components/form/formValidator/password";
import emailValidator    from "@components/form/formValidator/email";
import { EErrors } from '@interface/enums';
import { stringFormat } from '@tools/format';
import { LOCALSTORAGE } from '@tools/constants';
import { setStorage } from '@tools/localStorage';
import { passwordGenerator } from '@tools/passwordGenerator';

interface Props {
    token : string
    loginType?: "password"|"passwordless"
    plainForm?: boolean
    email?: string
    loginFn?: (data: {email: string, password?:string, code?:string, publicUrl: string}) => Promise<any>
    onLogin: (data: {token?: string, user?: IUser|any}) => void
}

enum EPasswordlessLoginStatus {
    "fill",
    "confirm"
}

const LoginForm: React.FC<Props> = (props) => {
    const {onLogin, token, loginType, plainForm, email, loginFn} = props;
    const [loginError, setLoginError] = useState<{error: number, message: string, reason?:string}|null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [loginStatus, setLoginStatus] = useState(EPasswordlessLoginStatus.fill);
    const [form] = Form.useForm();
    const {t} = useTranslation()
    const {api, showServerError} = useApi()

    const beforeLogin = useCallback(async () => {
        let data: any = await valueExtractor({form});

        if (!data)
            return;

        await login(data)
    }, [form])

    const login = useCallback(async (data) => {
        setLoginError(null);
        setConfirmLoading(true);
        let res;
        try {
            if (process.env.REACT_APP_LOGIN_NOISE)
                data.noise = process.env.REACT_APP_LOGIN_NOISE
            data.publicUrl = window.location.origin + process.env.PUBLIC_URL || ""

            res =  loginFn ? await loginFn?.(data) : await api.user.loginUser(data);

            const result = res.data
            setConfirmLoading(false);

            if(res.error)
                setLoginError(res)

            if (!result?.success) {
                setLoginError(result)
                if(result.error === EErrors.accessCodeAlreadySent) {
                    setLoginStatus(EPasswordlessLoginStatus.confirm)
                }

            }
            else if (result.success && result.data?.token && result.data?.user) {
                onLogin(result.data);
            }
            else if (result.success && result.data?.taskId) {
                setLoginStatus(EPasswordlessLoginStatus.confirm)
                const waitForToken = (event: any) => {
                    if (event.key === LOCALSTORAGE.TOKEN) {
                        window.removeEventListener('storage', waitForToken)
                        setStorage(LOCALSTORAGE.PASSWORDLESS_CONFIRM, passwordGenerator(8))
                        window.location.href = process.env.PUBLIC_URL || "/"
                    }
                };
    
                window.addEventListener('storage', waitForToken)
            }
        }
        catch(e: any) {
            setConfirmLoading(false);
            if(e.error)
                showServerError(e);
        }
    }, [form, onLogin, api, loginFn]);

    const onSendLinkAgain = useCallback(async() => {
        setLoginError(null)
        await login({email})
    }, [email])

    const errorMessage = useMemo(() => {
        let message = ""

        if(loginError && loginError?.error === EErrors.accessCodeAlreadySent)
            message = stringFormat(t("login:message.accessCodeAlreadySent"), [( 5 - Math.floor((new Date().getTime() - new Date(loginError.reason!).getTime()) / (1000 * 60))).toString()])
        else if(loginError && loginError?.error === EErrors.tooManyAttempts)
            message = stringFormat(t("login:message.tooManyAttempts"), [( 10 - Math.floor((new Date().getTime() - new Date(loginError.reason!).getTime()) / (1000 * 60))).toString()])
        else if(loginError && loginError?.error === EErrors.accessCodeAlreadyUsed)
            message = t("login:message.accessCodeAlreadyUsed")
        else if(loginError?.message)
            message=loginError?.message
        else if(loginError)
            message=t("login:message.login_error")

        if( message )
            return <Alert message={message} type="error" data-test={selector.login.alert}/>

        return null
    }, [loginError])

    const formContent = useMemo(() => {
        return <Flex vertical={true} gap={20}>
            {errorMessage}

            <Form
                autoComplete="off"
                layout={"vertical"}
                name="login"
                initialValues={{ remember: false, email: email || "" }}
                form={form}
                onFinish={beforeLogin}
            >
                <Form.Item
                    label={t("login:label.email")}
                    name="email"
                    {...loginStatus !== EPasswordlessLoginStatus.fill && {style: {display: "none"}}}
                    rules={[
                        {
                            required: true,
                            message: t("formError:required")
                        },
                        {
                            validator: (el, value) => emailValidator({value, t})
                        }
                    ]}
                >
                    <Input placeholder={t("login:placeholder.email")} autoComplete="off" data-test={selector.login.input.email}/>
                </Form.Item>

                {(!loginType || loginType === "password") && <Form.Item
                    label={t("login:label.password")}
                    name="password"
                    rules={[
                        {
                            required: true,
                            message: t("formError:required")
                        },
                        {
                            validator: (el, value) => passwordValidator({value, t})
                        }
                    ]}
                >
                    <Input.Password  placeholder={t("login:placeholder.password")} autoComplete="off"  data-test={selector.login.input.password}/>
                </Form.Item>}

                {(loginType === "passwordless" && loginStatus === EPasswordlessLoginStatus.confirm) && <Flex vertical={true} gap={10}>
                    <Typography.Text style={{textAlign: "center", color: "#a0a0a0"}}>
                        {stringFormat(t("login:message.continueLogin"), [email as string])}
                    </Typography.Text>
                    <Typography.Text style={{textAlign: "center", color: "#a0a0a0"}}>
                        {t("login:message.haveCode")}
                    </Typography.Text>
                    <Typography.Text style={{textAlign: "center", color: "#a0a0a0"}}>
                        {stringFormat(t("login:message.enterCode"), [email as string])}
                    </Typography.Text>
                    <Form.Item
                    label={t("login:label.code")}
                    name="code"
                    rules={[
                        {
                            required: true,
                            message: t("formError:required")
                        }
                    ]}
                >
                    <Input maxLength={6} style={{width: "100%"}} placeholder={t("login:placeholder.code")} autoComplete="off"  data-test={selector.login.input.code}/>
                </Form.Item>
                </Flex>
                }

                {loginStatus === EPasswordlessLoginStatus.fill && <Form.Item>
                    <Button htmlType="submit" type="primary" loading={confirmLoading} data-test={selector.login.button}>
                        {loginType === "passwordless" ? t("login:label.signInEmail") : t("login:button.log_in")}
                    </Button>
                </Form.Item>}
                {loginStatus === EPasswordlessLoginStatus.confirm && 
                    <Flex vertical={true}>
                        <Button htmlType="submit" type="primary" loading={confirmLoading}>
                            {t("login:label.verifyEmail")}
                        </Button>
                        <Typography.Text style={{textAlign: "center", color: "c0c0c0"}}>
                            {t("login:message.noEmail")} <Button type="link" size="small" onClick={onSendLinkAgain}>{t("login:label.sendAgain")}</Button>
                        </Typography.Text>
                    </Flex>
                }
            </Form>
        </Flex>
    },[errorMessage, form, loginType, email, loginStatus])

    return plainForm ? formContent : (
        <Modal
            centered={true}
            closable={false}
            title={t("login:title")}
            open={!token}
            footer={null}
            data-test={selector.login.modal}
        >
            {formContent}
        </Modal>
    )
};

export default LoginForm;
